import { ErrorMessage, Field, FieldArray, FormikHelpers } from "formik";
import { useParams } from "react-router-dom";
import {
  geSupplierProductDataWithRemainingQty,
  geSupplierProductRemainingQty,
} from "../../../../services/_sales";
import { useEffect, useState, useRef } from "react";

interface SupplierProductsProps {
  values: any;
  productList: any[];
  rowPurchaseOrders: any;
  supplierList: any[];
  setSupplierList: any;
  setRowPurchaseOrders: any;
  unitList: any[];
  setFieldValue: FormikHelpers<any>["setFieldValue"];
  setFieldError: FormikHelpers<any>["setFieldError"];
  errors: any;
  supplierProductsQtyData: any[];
  setSupplierProductsQtyData: any;
}

const SupplierProducts: React.FC<SupplierProductsProps> = ({
  values,
  productList,
  rowPurchaseOrders,
  setRowPurchaseOrders,
  supplierList,
  setSupplierList,
  unitList,
  setFieldValue,
  setFieldError,
  errors,
  supplierProductsQtyData,
  setSupplierProductsQtyData,
}) => {
  const [purchaseData, setPurchaseData] = useState<any[]>([]);
  const { id } = useParams();

  const selectedProducts = values.sales_invoice_products.reduce(
    (acc: any, p: any) => {
      const product = productList.find((pr: any) => pr.id === p.product_id);
      if (product) {
        acc[p.product_id] = { ...p, name: product.name };
      }
      return acc;
    },
    {}
  );

  const productsIds = JSON.stringify(
    values.sales_invoice_products.map((p: any) => p.product_id).sort()
  );

  useEffect(() => {
    const productIds = Object.values(selectedProducts).map(
      (val: any) => val.product_id
    );

    if (productIds.length === 0) return;

    const getPurchaseData = async () => {
      let query1 = "";
      if (id) {
        query1 = `sales_invoice_id=${id}`;
      }

      const result: any = await geSupplierProductDataWithRemainingQty(query1, {
        product_id: productIds,
      });

      if (result?.isSuccess) {
        const allPurchaseData = result.data;
        setPurchaseData(allPurchaseData);

        if (allPurchaseData.length === 0) {
          setRowPurchaseOrders({});
          setSupplierProductsQtyData([]);
          return;
        }

        const newRowPurchaseOrders: Record<number, any[]> = {};
        values.sales_invoice_supplier_products.forEach(
          (row: any, idx: number) => {
            if (row.product_id) {
              const filtered = allPurchaseData
                .filter((val: any) => val.product_id === row.product_id)
                .map((val: any) => ({
                  purchase_id: val.purchase_id,
                  invoice_no: val.invoice_no,
                  supplier_id: val.supplier_id,
                  supplier_name: val.supplier?.name || "",
                }));
              newRowPurchaseOrders[idx] = filtered;
            }
          }
        );
        setRowPurchaseOrders(newRowPurchaseOrders);

        // Prefill supplierProductsQtyData
        const initialQtyData: any[] = [];
        values.sales_invoice_supplier_products.forEach(
          (row: any, idx: number) => {
            const matched = allPurchaseData.find(
              (val: any) =>
                val.purchase_id === row.purchase_id &&
                val.product_id === row.product_id
            );

            if (matched) {
              setFieldValue(
                `sales_invoice_supplier_products[${idx}].supplier_id`,
                matched.supplier_id || row.purchase?.supplier_id || ""
              );

              const existing = initialQtyData.find(
                (i: any) => i.product_id === row.product_id
              );
              if (existing) {
                existing.purchases[row.purchase_id] = {
                  remainingQty: matched.remainingQty || 0,
                  supplier_id: matched.supplier_id || row.supplier_id || null,
                };
              } else {
                initialQtyData.push({
                  product_id: row.product_id,
                  purchases: {
                    [row.purchase_id]: {
                      remainingQty: matched.remainingQty || 0,
                      supplier_id:
                        matched.supplier_id || row.supplier_id || null,
                    },
                  },
                });
              }
            }
          }
        );

        setSupplierProductsQtyData(initialQtyData);

        const uniqueSupplierIds = new Set(supplierList.map((s) => s.id));
        const additionalSuppliers: any[] = [];

        values.sales_invoice_supplier_products.forEach((row: any) => {
          const supplier_id = row.supplier_id;
          const supplier_name = row.supplier_name;

          if (supplier_id && !uniqueSupplierIds.has(supplier_id)) {
            const supplierObj = {
              id: supplier_id,
              name: supplier_name || "Unknown",
            };
            additionalSuppliers.push(supplierObj);
            uniqueSupplierIds.add(supplier_id);
          }
        });

        if (additionalSuppliers.length > 0) {
          setSupplierList((prev: any[]) => [...prev, ...additionalSuppliers]);
        }
      }
    };

    getPurchaseData();
  }, [productsIds, productList]);

  const prevProductIdsRef = useRef<string[]>([]);

  useEffect(() => {
    const currentProductIds = values.sales_invoice_products
      .map((p: any) => p.product_id)
      .filter(Boolean);

    const removedProductIds = prevProductIdsRef.current.filter(
      (prevId) => !currentProductIds.includes(prevId)
    );

    if (removedProductIds.length > 0) {
      // Reset rowPurchaseOrders for removed products
      setRowPurchaseOrders((prev: any) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((key: string) => {
          const row = values.sales_invoice_supplier_products[key];
          if (removedProductIds.includes(row?.product_id)) {
            delete updated[key];
          }
        });
        return updated;
      });

      // Reset supplierProductsQtyData for removed products
      setSupplierProductsQtyData((prev: any[]) =>
        prev.filter((item) => !removedProductIds.includes(item.product_id))
      );

      // Reset form fields for removed products
      removedProductIds.forEach((removedId) => {
        values.sales_invoice_supplier_products.forEach(
          (row: any, index: number) => {
            if (row.product_id === removedId) {
              setFieldValue(
                `sales_invoice_supplier_products[${index}].purchase_id`,
                ""
              );
              setFieldValue(
                `sales_invoice_supplier_products[${index}].supplier_id`,
                ""
              );
              setFieldValue(
                `sales_invoice_supplier_products[${index}].quantity`,
                ""
              );
            }
          }
        );
      });
    }

    // Store current product IDs for next comparison
    prevProductIdsRef.current = currentProductIds;
  }, [productsIds]);

  const handlePurchaseOrderChange = async (
    values: any,
    index: any,
    selectedInvoiceNo: any
  ) => {
    const { purchase_id, product_id } =
      values.sales_invoice_supplier_products[index];

    const purchaseProduct = purchaseData.find(
      (val: any) => val.purchase_id === selectedInvoiceNo
    );
    setSupplierProductsQtyData((prevState: any) => {
      const updatedSupplierProductsQtyData = [...prevState];

      // Check product id data is exist or not
      const productIndex = updatedSupplierProductsQtyData.findIndex(
        (item: any) => item.product_id === product_id
      );

      if (productIndex !== -1) {
        updatedSupplierProductsQtyData[productIndex].purchases[purchase_id] = {
          remainingQty: purchaseProduct?.remainingQty || 0,
        };
      } else {
        updatedSupplierProductsQtyData.push({
          product_id,
          purchases: {
            [purchase_id]: {
              remainingQty: purchaseProduct?.remainingQty || 0,
            },
          },
        });
      }

      return updatedSupplierProductsQtyData;
    });

    if (purchaseProduct?.remainingQty === 0) {
      setFieldValue(`sales_invoice_supplier_products[${index}].quantity`, 0);
      return;
    }

    let totalQty = 0;
    values.sales_invoice_supplier_products.forEach((product: any, idx: any) => {
      if (idx !== index && product.product_id === product_id) {
        totalQty += product.quantity;
      }
    });

    if (parseFloat(selectedProducts[product_id].quantity) > totalQty) {
      const neededQty = selectedProducts[product_id].quantity - totalQty;

      if (purchaseProduct.remainingQty > neededQty) {
        setFieldValue(
          `sales_invoice_supplier_products[${index}].quantity`,
          neededQty || ""
        );
      } else {
        setFieldValue(
          `sales_invoice_supplier_products[${index}].quantity`,
          purchaseProduct.remainingQty || ""
        );
      }
    } else {
      // TODO :- handle if product's all quantity added
      setFieldValue(`sales_invoice_supplier_products[${index}].quantity`, 0);
    }
  };

  const validateSupplierProducts = (quantityValue: any, index: any) => {
    const currentIndex = values.sales_invoice_supplier_products[index];
    const selectedProduct = selectedProducts[currentIndex.product_id];

    let totalQty = 0;
    values.sales_invoice_supplier_products.forEach(
      (product: any, idx: number) => {
        if (product.product_id === currentIndex.product_id) {
          if (idx !== index) {
            totalQty += parseFloat(product.quantity);
          } else {
            totalQty += parseFloat(quantityValue) || 0;
          }
        }
      }
    );
    if (selectedProduct && totalQty > parseFloat(selectedProduct.quantity)) {
      return `Quantity can not be grater then selected product's quantity ${parseFloat(
        selectedProduct.quantity
      )}`;
    }
    return;
  };

  const isPurchaseOrderDisable = (purchaseId: any, index: any) => {
    const currentIndex = values.sales_invoice_supplier_products[index];

    return (
      // Disable if the purchase order and product both are already used
      values.sales_invoice_supplier_products.some(
        (product: any) =>
          product.purchase_id === purchaseId &&
          product.product_id === currentIndex.product_id
      ) &&
      // But don't disable the currently selected one
      currentIndex?.purchase_id !== purchaseId
    );
  };

  return (
    <div className="card my-card">
      <FieldArray name="sales_invoice_supplier_products">
        {({ remove, push }) => (
          <>
            <div className="card-header my-card-header bg-lighter d-flex justify-content-between">
              <div>
                <h6 className="my-card-header-head">Supplier's Products</h6>
              </div>
              <div>
                <button
                  title="Add Supplier Product"
                  className="btn btn-sm btn-primary waves-effect waves-light p-0"
                  type="button"
                  style={{ margin: "5px" }}
                  onClick={() => {
                    const newValues = {
                      sales_invoice_id: "",
                      supplier_id: "",
                      purchase_id: "",
                      product_id: "",
                      quantity: "",
                    };
                    push(newValues);
                  }}
                >
                  <span>
                    <i className="ti ti-plus my-card-icon-button"></i>
                  </span>
                </button>
              </div>
            </div>

            {values.sales_invoice_supplier_products?.length > 0 &&
              values.sales_invoice_supplier_products.map(
                (_: any, index: number) => {
                  return (
                    <div
                      className="card-body my-card-body"
                      style={{ paddingBottom: 0, paddingTop: 0 }}
                      key={index}
                    >
                      <div className="sales_section_card">
                        <div className="row g-3 m-0">
                          {/* Product Select */}
                          <div className="col-12 col-sm-6 col-md-4">
                            <div className="d-flex justify-content-between">
                              <div>
                                <label
                                  htmlFor={`sales_invoice_supplier_products[${index}].product_id`}
                                  className="form-label"
                                  style={{ fontSize: "0.9375rem" }}
                                >
                                  Product:{" "}
                                  <span className="text-danger">*</span>
                                </label>
                              </div>
                            </div>
                            <Field
                              as="select"
                              name={`sales_invoice_supplier_products[${index}].product_id`}
                              className="form-select"
                              onChange={(e: any) => {
                                const productId = e.target.value;
                                setFieldValue(e.target.name, productId);
                                setFieldValue(
                                  `sales_invoice_supplier_products[${index}].purchase_id`,
                                  ""
                                );
                                setFieldValue(
                                  `sales_invoice_supplier_products[${index}].supplier_id`,
                                  ""
                                );
                                setFieldValue(
                                  `sales_invoice_supplier_products[${index}].quantity`,
                                  ""
                                );

                                const filteredInvoices = purchaseData
                                  .filter(
                                    (val: any) => val.product_id === productId
                                  )
                                  .map((val: any) => ({
                                    purchase_id: val.purchase_id,
                                    invoice_no: val.invoice_no,
                                    supplier_id: val.supplier_id,
                                  }));

                                // Set row-specific purchase orders
                                setRowPurchaseOrders((prev: any) => ({
                                  ...prev,
                                  [index]: filteredInvoices,
                                }));
                              }}
                            >
                              <option value="">Select Product</option>
                              {Object.keys(selectedProducts).map(
                                (productId) => {
                                  const product = selectedProducts[productId];
                                  return (
                                    <option
                                      key={productId}
                                      value={product.product_id}
                                    >
                                      {product.name}
                                    </option>
                                  );
                                }
                              )}
                            </Field>
                            <ErrorMessage
                              name={`sales_invoice_supplier_products[${index}].product_id`}
                              component="div"
                              className="text-danger"
                            />
                          </div>

                          {/* Purchase Order Select */}
                          <div className="col-12 col-sm-6 col-md-4">
                            <div className="d-flex justify-content-between">
                              <div>
                                <label
                                  htmlFor={`sales_invoice_supplier_products[${index}].purchase_id`}
                                  className="form-label"
                                  style={{ fontSize: "0.9375rem" }}
                                >
                                  Purchase Invoice-No:{" "}
                                  <span className="text-danger">*</span>
                                </label>
                              </div>
                            </div>
                            <Field
                              as="select"
                              name={`sales_invoice_supplier_products[${index}].purchase_id`}
                              className="form-select"
                              value={
                                values.sales_invoice_supplier_products[index]
                                  .purchase_id || ""
                              }
                              onChange={(e: any) => {
                                const selectedInvoiceNo = e.target.value;
                                setFieldValue(
                                  `sales_invoice_supplier_products[${index}].purchase_id`,
                                  selectedInvoiceNo || ""
                                );
                                values.sales_invoice_supplier_products[
                                  index
                                ].purchase_id = selectedInvoiceNo || "";

                                if (selectedInvoiceNo) {
                                  const purchaseOrder = (
                                    rowPurchaseOrders?.[index] ?? []
                                  ).find(
                                    (invoice: any) =>
                                      invoice.purchase_id === selectedInvoiceNo
                                  );

                                  if (purchaseOrder) {
                                    setFieldValue(
                                      `sales_invoice_supplier_products[${index}].supplier_id`,
                                      purchaseOrder.supplier_id
                                    );
                                  }

                                  handlePurchaseOrderChange(
                                    values,
                                    index,
                                    selectedInvoiceNo
                                  );
                                } else {
                                  setFieldValue(
                                    `sales_invoice_supplier_products[${index}].supplier_id`,
                                    ""
                                  );
                                  setFieldValue(
                                    `sales_invoice_supplier_products[${index}].quantity`,
                                    ""
                                  );
                                }
                              }}
                            >
                              <option value="">Select Purchase Order</option>
                              {(rowPurchaseOrders[index] || []).map(
                                (invoice: any) => (
                                  <option
                                    key={invoice.purchase_id}
                                    value={invoice.purchase_id}
                                    disabled={isPurchaseOrderDisable(
                                      invoice.purchase_id,
                                      index
                                    )}
                                  >
                                    {invoice.invoice_no}
                                  </option>
                                )
                              )}
                            </Field>

                            <ErrorMessage
                              name={`sales_invoice_supplier_products[${index}].purchase_id`}
                              component="div"
                              className="text-danger"
                            />
                          </div>

                          {/* Supplier Select */}
                          <div className="col-12 col-sm-6 col-md-4">
                            <div className="d-flex justify-content-between">
                              <div>
                                <label
                                  htmlFor={`sales_invoice_supplier_products[${index}].supplier_id`}
                                  className="form-label"
                                  style={{ fontSize: "0.9375rem" }}
                                >
                                  Supplier:{" "}
                                  <span className="text-danger">*</span>
                                </label>
                              </div>
                            </div>
                            <Field
                              as="select"
                              name={`sales_invoice_supplier_products[${index}].supplier_id`}
                              className="form-select"
                              value={
                                values.sales_invoice_supplier_products[index]
                                  .supplier_id
                              }
                              onChange={() => {}}
                              disabled
                            >
                              <option value="">Supplier</option>
                              {supplierList.map((supplier: any) => (
                                <option key={supplier.id} value={supplier.id}>
                                  {supplier.name}
                                </option>
                              ))}
                            </Field>
                            <ErrorMessage
                              name={`sales_invoice_supplier_products[${index}].supplier_id`}
                              component="div"
                              className="text-danger"
                            />
                          </div>

                          <div className="col-12 col-sm-6 col-md-4">
                            <label
                              className="form-label"
                              style={{ fontSize: "0.9375rem" }}
                            >
                              Quantity (MTS):
                            </label>
                            <Field
                              type="number"
                              name={`sales_invoice_supplier_products[${index}].quantity`}
                              className="form-control"
                              placeholder="Enter Quantity"
                              disabled={
                                !values.sales_invoice_supplier_products[index]
                                  .purchase_id ||
                                !values.sales_invoice_supplier_products[index]
                                  .product_id
                              }
                              values={
                                values.sales_invoice_supplier_products[index]
                                  .quantity || ""
                              }
                              validate={(value: any) =>
                                validateSupplierProducts(value, index)
                              }
                              onChange={(e: any) => {
                                const quantity = e.target.value;
                                const currentIndex =
                                  values.sales_invoice_supplier_products[index];

                                // Quantity not grater then purchase's product's remaining quantity
                                if (
                                  supplierProductsQtyData &&
                                  supplierProductsQtyData?.length > 0
                                ) {
                                  const supplierProduct =
                                    supplierProductsQtyData.find(
                                      (item: any) =>
                                        item.product_id ===
                                        currentIndex.product_id
                                    )?.purchases;
                                  if (
                                    parseFloat(quantity) >
                                    parseFloat(
                                      supplierProduct[currentIndex.purchase_id]
                                        .remainingQty
                                    )
                                  ) {
                                    setFieldError(
                                      `sales_invoice_supplier_products[${index}].quantity`,
                                      `Quantity can not be grater than  purchase order's product's remaining quantity,purchase order's product's all quantity used.`
                                    );
                                    return;
                                  }
                                }

                                // Default store values
                                setFieldValue(
                                  e.target.name,
                                  parseFloat(quantity) || ""
                                );
                              }}
                            />

                            {errors.sales_invoice_supplier_products &&
                              errors.sales_invoice_supplier_products[index] &&
                              errors.sales_invoice_supplier_products[index]
                                .quantity && (
                                <div className="text-danger">
                                  {
                                    errors.sales_invoice_supplier_products[
                                      index
                                    ].quantity
                                  }
                                </div>
                              )}
                          </div>

                          <i
                            className="fa-solid fa-xmark sales_document_btn"
                            onClick={() => remove(index)}
                          ></i>
                        </div>
                      </div>
                    </div>
                  );
                }
              )}

            {values.sales_invoice_supplier_products?.length > 0 && (
              <div className="row m-2 justify-content-end">
                <button
                  title="Add Document"
                  type="button"
                  className="btn btn-primary col-12 col-sm-auto"
                  onClick={() => {
                    const newValues = {
                      sales_invoice_id: "",
                      supplier_id: "",
                      purchase_id: "",
                      product_id: "",
                      quantity: "",
                    };
                    push(newValues);
                  }}
                >
                  Add Supplier's Product
                </button>
              </div>
            )}
          </>
        )}
      </FieldArray>
    </div>
  );
};

export default SupplierProducts;
